// ============================================================================
// The Quote Brain journey line (lucid 019 O1, Peter 2026-11-01: "it has to be
// accurate and perfect fit for what's happening in the background, not made
// up"). One line from Process files to a written quote. Each step, each fill
// and each movement comes from something that is happening:
//   - Files: this tab's own reading, counted (procState), then its processing.
//   - Summary: this tab writing the summary.
//   - Evidence, Scope, Pricing: the run's own tool calls, in the QuoteMaker
//     Method's working order (backend shared/quote-brain/prompt.js WORKING
//     ORDER: read the evidence, write_items, price_items then preflight,
//     finish). A step is reached by its first call and never goes back,
//     because a fix after a price check writes items again.
//   - Ready: the run's quote is written into the job and this tab holds it.
// Nothing moves unless this tab is reading or writing, or the server's last
// answer said the run is working. The steps of a run cannot be counted ahead,
// so only the files have a fill: the rest show a step under way, not a share.
// ============================================================================

import { LIVE_RUN_STATUSES, runProgressOf, runStepLabel, type QuoteBrainAutoStart, type QuoteBrainRunDetail, type QuoteBrainRunProgress } from './quote-brain-runs';

export type JourneyStepId = 'files' | 'summary' | 'evidence' | 'scope' | 'pricing' | 'ready';

export const JOURNEY_STEPS: ReadonlyArray<{ id: JourneyStepId; label: string }> = Object.freeze([
    { id: 'files', label: 'Files' },
    { id: 'summary', label: 'Summary' },
    { id: 'evidence', label: 'Evidence' },
    { id: 'scope', label: 'Scope' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'ready', label: 'Ready' },
]);

const EVIDENCE = 2;
const SCOPE = 3;
const PRICING = 4;
const READY = JOURNEY_STEPS.length - 1;

export interface JourneyInput {
    /** This tab's Stage 1 count of files read, of files sent (procState). */
    files?: { processed: number; total: number } | null;
    /** This tab's Stage 1 is reading or processing the job's files (isStage1Processing). */
    filesProcessing?: boolean;
    /** This tab is writing the job's summary. */
    summarizing?: boolean;
    hasSummary?: boolean;
    /** Whether the server starts the job's run by itself, as the runs list last said. */
    autoStart?: QuoteBrainAutoStart['state'] | null;
    /** The run the bar follows, as the server last reported it. */
    detail?: QuoteBrainRunDetail | null;
    /** The run's quote is written into the job and this tab holds it. */
    delivered?: boolean;
}

export interface JourneyModel {
    /** Index into JOURNEY_STEPS of the step under way, or READY once the quote is in. */
    active: number;
    /** 0 to 1 when the step is counted (the files), else null. */
    fill: number | null;
    /** What is happening now, in the builder's words, as a sentence without its full stop. */
    activity: string;
    /** The work is on the server, so the tab can close. */
    onServer: boolean;
    /** Something is working now: this tab reads or writes, or the server said the run works. */
    moving: boolean;
    done: boolean;
    /** Changes each time something real happens: a file read, a step reached, a turn of the run finished. */
    beat: string;
}

function sentence(words: string): string {
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : words;
}

const STEP_BY_REACHED: Record<QuoteBrainRunProgress['reached'], number> = { evidence: EVIDENCE, scope: SCOPE, pricing: PRICING };

/** The furthest step of the method the run's tool calls have reached (Evidence until the first item is written). */
export function runReachedStep(detail: QuoteBrainRunDetail | null | undefined): number {
    return STEP_BY_REACHED[runProgressOf(detail).reached] ?? EVIDENCE;
}

export function quoteBrainJourney(input: JourneyInput): JourneyModel {
    const run = input.detail?.run || null;
    if (run) {
        const progress = runProgressOf(input.detail);
        // The run's record changes at each turn's start and end (checkpoints-pg.js), so its time marks each real change.
        const beat = `${run.status}:${run.updatedAt || ''}:${progress.doing || ''}`;
        if (input.delivered) {
            return { active: READY, fill: null, activity: 'Quote ready', onServer: true, moving: false, done: true, beat: `ready:${beat}` };
        }
        // The run finished and its quote is on the way into the job (delivery, then this tab's load).
        if (run.status === 'complete') {
            return { active: READY, fill: null, activity: 'Putting the quote into the job', onServer: true, moving: true, done: false, beat: `ready:${beat}` };
        }
        const step = runReachedStep(input.detail);
        const live = LIVE_RUN_STATUSES.has(run.status);
        // After a price check, an item written, changed or removed is a fix (WORKING ORDER 5), then it prices again.
        const activity = run.stopRequestedAt && live
            ? 'Stopping'
            : progress.fixing ? 'Fixing the items after the price check' : sentence(runStepLabel(input.detail || null));
        return { active: step, fill: null, activity, onServer: true, moving: live, done: false, beat: `${step}:${beat}` };
    }
    const total = Math.max(0, Number(input.files?.total) || 0);
    const processed = Math.min(total, Math.max(0, Number(input.files?.processed) || 0));
    if (total > 0 && processed < total) {
        return {
            active: 0,
            fill: processed / total,
            activity: `Reading ${processed} of ${total} file${total === 1 ? '' : 's'}`,
            onServer: false,
            moving: true,
            done: false,
            beat: `files:${processed}`,
        };
    }
    if (input.filesProcessing) {
        return { active: 0, fill: total > 0 ? 1 : null, activity: 'Processing the files', onServer: false, moving: true, done: false, beat: 'processing' };
    }
    if (input.summarizing) {
        return { active: 1, fill: null, activity: 'Writing the summary', onServer: false, moving: true, done: false, beat: 'summary' };
    }
    // The server has the job's summary ('waiting' says it picked the job up), so its start is under way.
    if (input.hasSummary || input.autoStart === 'waiting') {
        return {
            active: EVIDENCE,
            fill: null,
            activity: input.autoStart === 'waiting' ? 'Waiting for the files to reach the server' : 'Starting on the server',
            onServer: true,
            moving: true,
            done: false,
            beat: `start:${input.autoStart || ''}`,
        };
    }
    // Nothing read yet: the files are next, and nothing moves.
    return { active: 0, fill: null, activity: 'Starts once the files are read', onServer: false, moving: false, done: false, beat: 'idle' };
}
